import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Search, 
  Copy, 
  Check, 
  ExternalLink, 
  QrCode, 
  Download,
  X,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  ArrowLeft,
  Shield,
  Save,
  FileDown,
  AlertCircle,
  Globe,
  BookOpen,
  Monitor,
  FileText,
  GraduationCap,
  Building2,
  Lock,
  Scale,
  Layers
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { siteRoutes, ROUTE_GROUPS } from '../data/siteRoutes';

const BASE_URL = 'https://thirdcultureleadership.com';

// Icon mapping for groups
const groupIcons = {
  [ROUTE_GROUPS.CORE]: Globe,
  [ROUTE_GROUPS.BOOKS]: BookOpen,
  [ROUTE_GROUPS.BOOK_READER]: Monitor,
  [ROUTE_GROUPS.ARTICLES]: FileText,
  [ROUTE_GROUPS.COURSES]: GraduationCap,
  [ROUTE_GROUPS.PUBLISHING]: Building2,
  [ROUTE_GROUPS.ADMIN]: Shield,
  [ROUTE_GROUPS.AUTH]: Lock,
  [ROUTE_GROUPS.LEGAL]: Scale,
  [ROUTE_GROUPS.OTHER]: Layers
};

const AdminLinksManager = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, admin, isLoading, token } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showPublicOnly, setShowPublicOnly] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [apiLoading, setApiLoading] = useState(true);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    path: '',
    group: ROUTE_GROUPS.OTHER,
    description: '',
    isPublic: true
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  // Check authentication
  useEffect(() => {
    if (!isLoading && (!isAuthenticated() || !isAdmin())) {
      navigate('/admin/login');
    }
  }, [isLoading, isAuthenticated, isAdmin, navigate]);

  // Load links from backend API
  useEffect(() => {
    const loadLinks = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/links/admin/list`, { headers });
        if (res.ok) {
          const data = await res.json();
          setRoutes(data.links.map(l => ({
            id: l.id, name: l.name, path: l.path,
            group: l.group, description: l.description || '',
            isPublic: l.is_public,
          })));
        } else {
          // Fallback: seed defaults then reload
          await fetch(`${API_URL}/api/links/admin/seed`, { method: 'POST', headers });
          const res2 = await fetch(`${API_URL}/api/links/admin/list`, { headers });
          if (res2.ok) {
            const data = await res2.json();
            setRoutes(data.links.map(l => ({
              id: l.id, name: l.name, path: l.path,
              group: l.group, description: l.description || '',
              isPublic: l.is_public,
            })));
          }
        }
      } catch (e) {
        setRoutes([...siteRoutes]);
      }
      setApiLoading(false);
    };
    loadLinks();
  }, [token]);

  // Get unique groups
  const groups = useMemo(() => {
    return Object.values(ROUTE_GROUPS);
  }, []);

  // Filter routes
  const filteredRoutes = useMemo(() => {
    return routes.filter(route => {
      const matchesSearch = !searchQuery || 
        route.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        route.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
        route.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesGroup = selectedGroup === 'all' || route.group === selectedGroup;
      const matchesVisibility = !showPublicOnly || route.isPublic;
      
      return matchesSearch && matchesGroup && matchesVisibility;
    });
  }, [routes, searchQuery, selectedGroup, showPublicOnly]);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getFullUrl = (path) => `${BASE_URL}${path}`;

  const exportCSV = () => {
    const headers = ['Name', 'URL', 'Group', 'Description', 'Public'];
    const rows = routes.map(route => [
      route.name,
      getFullUrl(route.path),
      route.group,
      route.description,
      route.isPublic ? 'Yes' : 'No'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'site-links-directory.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAddRoute = async () => {
    try {
      const res = await fetch(`${API_URL}/api/links/admin/create`, {
        method: 'POST', headers,
        body: JSON.stringify({
          name: formData.name, path: formData.path,
          group: formData.group, description: formData.description,
          is_public: formData.isPublic,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const link = data.link;
        setRoutes(prev => [...prev, {
          id: link.id, name: link.name, path: link.path,
          group: link.group, description: link.description || '',
          isPublic: link.is_public,
        }]);
      }
    } catch (e) { console.error('Add failed:', e); }
    setAddModalOpen(false);
    setFormData({ name: '', path: '', group: ROUTE_GROUPS.OTHER, description: '', isPublic: true });
  };

  const handleEditRoute = async () => {
    try {
      await fetch(`${API_URL}/api/links/admin/${editModalOpen}`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          name: formData.name, path: formData.path,
          group: formData.group, description: formData.description,
          is_public: formData.isPublic,
        }),
      });
      setRoutes(prev => prev.map(route =>
        route.id === editModalOpen ? { ...route, ...formData } : route
      ));
    } catch (e) { console.error('Edit failed:', e); }
    setEditModalOpen(null);
    setFormData({ name: '', path: '', group: ROUTE_GROUPS.OTHER, description: '', isPublic: true });
  };

  const handleDeleteRoute = async (id) => {
    try {
      await fetch(`${API_URL}/api/links/admin/${id}`, { method: 'DELETE', headers });
      setRoutes(prev => prev.filter(route => route.id !== id));
    } catch (e) { console.error('Delete failed:', e); }
    setDeleteConfirmId(null);
  };

  const openEditModal = (route) => {
    setFormData({
      name: route.name,
      path: route.path,
      group: route.group,
      description: route.description,
      isPublic: route.isPublic
    });
    setEditModalOpen(route.id);
  };

  const toggleVisibility = async (id) => {
    const route = routes.find(r => r.id === id);
    if (!route) return;
    try {
      await fetch(`${API_URL}/api/links/admin/${id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ is_public: !route.isPublic }),
      });
      setRoutes(prev => prev.map(r => r.id === id ? { ...r, isPublic: !r.isPublic } : r));
    } catch (e) { console.error('Toggle failed:', e); }
  };

  if (isLoading || apiLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated() || !isAdmin()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900">
      {/* Header */}
      <div className="bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin"
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                <ArrowLeft size={20} className="text-white" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="text-gold" size={24} />
                  Links Manager
                </h1>
                <p className="text-sm text-slate-400">Manage site directory entries</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/links"
                target="_blank"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                <Eye size={16} />
                View Public Directory
              </Link>
              <span className="text-sm text-slate-400">
                Logged in as <span className="text-gold">{admin?.email}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="text-2xl font-bold text-white">{routes.length}</p>
            <p className="text-sm text-slate-400">Total Links</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="text-2xl font-bold text-green-400">{routes.filter(r => r.isPublic).length}</p>
            <p className="text-sm text-slate-400">Public Links</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="text-2xl font-bold text-amber-400">{routes.filter(r => !r.isPublic).length}</p>
            <p className="text-sm text-slate-400">Internal Links</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="text-2xl font-bold text-gold">{groups.length}</p>
            <p className="text-sm text-slate-400">Categories</p>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white/5 rounded-2xl border border-white/10 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search links..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50 transition-all"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Group Filter */}
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-gold/50 cursor-pointer"
              >
                <option value="all">All Categories</option>
                {groups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>

              {/* Public Only Toggle */}
              <button
                onClick={() => setShowPublicOnly(!showPublicOnly)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-colors ${
                  showPublicOnly 
                    ? 'bg-green-500/20 border-green-500/30 text-green-400' 
                    : 'bg-white/5 border-white/10 text-slate-400'
                }`}
              >
                {showPublicOnly ? <Eye size={18} /> : <EyeOff size={18} />}
                Public Only
              </button>

              {/* Export CSV */}
              <button
                onClick={exportCSV}
                className="flex items-center gap-2 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                <FileDown size={18} />
                Export CSV
              </button>

              {/* Add New */}
              <button
                onClick={() => setAddModalOpen(true)}
                className="flex items-center gap-2 px-4 py-3 bg-gold text-navy rounded-xl hover:bg-gold/90 transition-colors font-medium"
              >
                <Plus size={18} />
                Add Link
              </button>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-white/10 text-sm text-slate-400">
            Showing {filteredRoutes.length} of {routes.length} links
          </div>
        </div>

        {/* Links Table */}
        <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Name</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Path</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Group</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-slate-400">Visibility</th>
                  <th className="text-center px-6 py-4 text-sm font-medium text-slate-400">QR</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredRoutes.map(route => {
                  const GroupIcon = groupIcons[route.group] || Layers;
                  return (
                    <tr key={route.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{route.name}</p>
                          <p className="text-xs text-slate-500 line-clamp-1">{route.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-sm text-slate-400 bg-white/5 px-2 py-1 rounded">
                          {route.path}
                        </code>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <GroupIcon size={16} className="text-slate-400" />
                          <span className="text-sm text-slate-400">{route.group}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleVisibility(route.id)}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            route.isPublic 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}
                        >
                          {route.isPublic ? <Eye size={12} /> : <EyeOff size={12} />}
                          {route.isPublic ? 'Public' : 'Internal'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => setQrModalOpen(route.id)}
                          className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                        >
                          <QrCode size={18} className="text-slate-400" />
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => copyToClipboard(getFullUrl(route.path), route.id)}
                            className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                            title="Copy Link"
                          >
                            {copiedId === route.id ? (
                              <Check size={16} className="text-green-400" />
                            ) : (
                              <Copy size={16} className="text-slate-400" />
                            )}
                          </button>
                          <button
                            onClick={() => openEditModal(route)}
                            className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={16} className="text-slate-400" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(route.id)}
                            className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </button>
                          <Link
                            to={route.path}
                            target="_blank"
                            className="p-2 bg-gold/20 rounded-lg hover:bg-gold/30 transition-colors"
                            title="Visit Page"
                          >
                            <ExternalLink size={16} className="text-gold" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredRoutes.length === 0 && (
            <div className="text-center py-16">
              <AlertCircle className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No links found</p>
            </div>
          )}
        </div>
      </div>

      {/* QR Modal */}
      {qrModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setQrModalOpen(null)} />
          <div className="relative bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-white/10">
            <button
              onClick={() => setQrModalOpen(null)}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X size={20} className="text-slate-400" />
            </button>

            {(() => {
              const route = routes.find(r => r.id === qrModalOpen);
              if (!route) return null;
              
              return (
                <>
                  <div className="text-center mb-6">
                    <h3 className="font-semibold text-white text-lg mb-1">{route.name}</h3>
                    <p className="text-sm text-slate-400">{route.path}</p>
                  </div>

                  <div className="flex justify-center mb-6">
                    <div className="p-4 bg-white rounded-2xl">
                      <QRCodeSVG
                        value={getFullUrl(route.path)}
                        size={200}
                        level="H"
                        includeMargin={false}
                        bgColor="#ffffff"
                        fgColor="#1a365d"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(getFullUrl(route.path), `modal-${route.id}`)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
                    >
                      {copiedId === `modal-${route.id}` ? <Check size={18} /> : <Copy size={18} />}
                      {copiedId === `modal-${route.id}` ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(addModalOpen || editModalOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setAddModalOpen(false); setEditModalOpen(null); }} />
          <div className="relative bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-white/10">
            <button
              onClick={() => { setAddModalOpen(false); setEditModalOpen(null); }}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X size={20} className="text-slate-400" />
            </button>

            <h3 className="font-semibold text-white text-xl mb-6">
              {addModalOpen ? 'Add New Link' : 'Edit Link'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Page Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50"
                  placeholder="e.g., My New Page"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Path</label>
                <input
                  type="text"
                  value={formData.path}
                  onChange={(e) => setFormData(prev => ({ ...prev, path: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50"
                  placeholder="e.g., /my-new-page"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Category</label>
                <select
                  value={formData.group}
                  onChange={(e) => setFormData(prev => ({ ...prev, group: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-gold/50 cursor-pointer"
                >
                  {groups.map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                  rows={3}
                  placeholder="Brief description of the page"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFormData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-colors ${
                    formData.isPublic 
                      ? 'bg-green-500/20 border-green-500/30 text-green-400' 
                      : 'bg-amber-500/20 border-amber-500/30 text-amber-400'
                  }`}
                >
                  {formData.isPublic ? <Eye size={16} /> : <EyeOff size={16} />}
                  {formData.isPublic ? 'Public' : 'Internal'}
                </button>
                <span className="text-sm text-slate-500">
                  {formData.isPublic ? 'Visible on public directory' : 'Admin only'}
                </span>
              </div>

              <button
                onClick={addModalOpen ? handleAddRoute : handleEditRoute}
                disabled={!formData.name || !formData.path}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-navy rounded-xl hover:bg-gold/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              >
                <Save size={18} />
                {addModalOpen ? 'Add Link' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-white/10">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Delete Link?</h3>
              <p className="text-slate-400 mb-6">
                This action cannot be undone. The link will be removed from the directory.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteRoute(deleteConfirmId)}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLinksManager;
